// eslint-disable-next-line no-restricted-imports
import { message } from 'antd';
import moment from 'moment';
import { createIntl } from 'react-intl';
import { put, select, takeLatest, delay } from 'redux-saga/effects';
import { toastService } from 'components/molecules/toast';
import { getTemplateDataUpdates } from 'components/organisms/templates/projects/gantt/config/template-data-updates';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { formatDate } from 'data/projects/gantt/dates';
import { getNewDependenciesEnabledSetting, getQueryAdvancedSettingsConfigKey } from 'queries/advanced-settings/advanced-settings';
import { logger } from 'services';
import { addDays, EntityType, getDateShiftsForEntity } from 'utils/date-shift';
import { getPhaseList, getSprintList, getTaskList, isValidDateShift } from 'utils/template-date-shift';
import { getTranslationCache } from 'utils/translation-loader';
import { updateSprintInProjectTemplate, updateTaskInProjectTemplate } from '../actions';
import { selectAllTemplateProjectPhase, selectAllTemplateSprint, selectAllTemplateTask } from '../selectors';
import { updateProjectTemplateTask, updateProjectTemplateSprint, updateProjectTemplateTasks, updateProjectTemplateProjectPhases, updateProjectTemplateSprints } from '../slices';
const intl = createIntl({
    locale: 'en',
    messages: getTranslationCache().get('en') || {}
});
function* onUpdateTask({ payload }) {
    try {
        const { taskId, payload: data } = payload;
        const { startDay, duration } = data;
        if (!startDay && !duration) {
            return;
        }
        const phases = yield select(selectAllTemplateProjectPhase());
        const tasks = yield select(selectAllTemplateTask());
        const sprints = yield select(selectAllTemplateSprint());
        const taskList = getTaskList(Object.values(tasks));
        const sprintList = getSprintList(Object.values(sprints));
        const phaseList = getPhaseList(Object.values(phases));
        const entity = taskList[taskId];
        const queryClient = yield getQueryClientContext();
        const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
        const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
        if (!entity) {
            logger.error('Task not found during date shift on task update', {
                error: 'Task not found',
                taskId
            });
            message.error('There was an error while updating the task');
            return;
        }
        const newStartDate = startDay ? formatDate(moment().add(startDay, 'day')) : entity.startDate;
        const newDueDate = duration ? formatDate(addDays(newStartDate, duration, true)) : entity.dueDate;
        const [phaseUpdates, taskUpdates, sprintUpdates, , dependencyRelationsToDelete] = getDateShiftsForEntity(getPhaseList(Object.values(phases)), taskList, sprintList, {
            id: taskId,
            type: EntityType.Task,
            depth: taskList[taskId].depth,
            ...startDay && {
                startDate: newStartDate
            },
            ...duration && {
                dueDate: newDueDate
            }
        }, {
            allowWeekend: true,
            alignParentWithChildren: true,
            enabledNewDependencies: newDependenciesSettingEnabled,
            fromTemplate: true
        });
        const isValid = isValidDateShift(phaseUpdates);
        if (!isValid || dependencyRelationsToDelete.length > 0) {
            var _tasks_taskId;
            yield put(updateProjectTemplateTask({
                task: {
                    taskId,
                    startDay: (_tasks_taskId = tasks[taskId]) === null || _tasks_taskId === void 0 ? void 0 : _tasks_taskId.startDay
                }
            }));
            if (dependencyRelationsToDelete.length > 0) {
                toastService({
                    title: intl.formatMessage({
                        id: 'failure'
                    }),
                    message: intl.formatMessage({
                        id: 'templates.date.shift.start.day.error.dependency'
                    })
                }).error();
            } else {
                toastService({
                    title: intl.formatMessage({
                        id: 'failure'
                    }),
                    message: intl.formatMessage({
                        id: 'templates.date.shift.start.day.error'
                    })
                }).error();
            }
            return;
        }
        /**
     * added delay of 1ms to nullify date shift changes from other actions
     * TODO: to refactor unnecessary actions/ handlers.
     */ yield delay(1);
        if (taskUpdates && Object.values(taskUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(taskUpdates, taskList);
            yield put(updateProjectTemplateTasks(dataUpdates));
        }
        if (phaseUpdates && Object.values(phaseUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(phaseUpdates, phaseList);
            yield put(updateProjectTemplateProjectPhases(dataUpdates));
        }
        if (sprintUpdates && Object.values(sprintUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(sprintUpdates, sprintList);
            yield put(updateProjectTemplateSprints(dataUpdates));
        }
    } catch (e) {
        logger.error('Exception on date shift on template task update', {
            error: e
        });
    }
}
export function* onCreateAndUpdateSprint({ payload }) {
    try {
        const { sprintId, payload: data } = payload;
        const { startDay, duration } = data;
        if (!startDay && !duration) {
            return;
        }
        const phases = yield select(selectAllTemplateProjectPhase());
        const tasks = yield select(selectAllTemplateTask());
        const sprints = yield select(selectAllTemplateSprint());
        const taskList = getTaskList(Object.values(tasks));
        const phaseList = getPhaseList(Object.values(phases));
        const sprintList = getSprintList(Object.values(sprints));
        const entity = sprintList[sprintId];
        const queryClient = yield getQueryClientContext();
        const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
        const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
        if (!entity) {
            logger.warn('Sprint not found during date shift on sprint update', {
                sprintId
            });
            return;
        }
        const newStartDate = startDay ? formatDate(moment().add(startDay, 'day')) : entity.startDate;
        const newDueDate = duration ? formatDate(addDays(newStartDate, duration, true)) : entity.dueDate;
        const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(getPhaseList(Object.values(phases)), taskList, sprintList, {
            id: sprintId,
            type: EntityType.Sprint,
            depth: sprintList[sprintId].depth,
            ...startDay && {
                startDate: newStartDate
            },
            ...duration && {
                dueDate: newDueDate
            }
        }, {
            allowWeekend: true,
            alignParentWithChildren: true,
            enabledNewDependencies: newDependenciesSettingEnabled,
            fromTemplate: true
        });
        const isValid = isValidDateShift(phaseUpdates);
        if (!isValid) {
            var _sprints_sprintId;
            yield put(updateProjectTemplateSprint({
                sprint: {
                    sprintId,
                    startDay: (_sprints_sprintId = sprints[sprintId]) === null || _sprints_sprintId === void 0 ? void 0 : _sprints_sprintId.startDay
                }
            }));
            toastService({
                title: intl.formatMessage({
                    id: 'failure'
                }),
                message: intl.formatMessage({
                    id: 'templates.date.shift.start.day.error'
                })
            }).error();
            return;
        }
        /**
     * added delay of 1ms to nullify date shift changes from other actions
     * TODO: to refactor unnecessary actions/ handlers.
     */ yield delay(1);
        if (taskUpdates && Object.values(taskUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(taskUpdates, taskList);
            yield put(updateProjectTemplateTasks(dataUpdates));
        }
        if (phaseUpdates && Object.values(phaseUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(phaseUpdates, phaseList);
            yield put(updateProjectTemplateProjectPhases(dataUpdates));
        }
        if (sprintUpdates && Object.values(sprintUpdates).length > 0) {
            const dataUpdates = getTemplateDataUpdates(sprintUpdates, sprintList);
            yield put(updateProjectTemplateSprints(dataUpdates));
        }
    } catch (e) {
        logger.error('Exception on date shift on template task update', {
            error: e
        });
    }
}
export const watchTemplateDateShiftSaga = function*() {
    yield takeLatest(updateTaskInProjectTemplate.type, onUpdateTask);
    yield takeLatest(updateSprintInProjectTemplate.type, onCreateAndUpdateSprint);
};
